import React, { useState, useEffect } from 'react';
import { FaCog, FaBell, FaClock, FaGlobe, FaSave, FaComments } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for school administrators to configure school settings
 */
const SchoolSettings = () => {
  const { showToast } = useToast();
  
  // State for settings data
  const [settings, setSettings] = useState({
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      classReminders: true,
      newStudentAlerts: true,
      paymentAlerts: true,
      marketingEmails: false,
      realTimeNotifications: false,
      pushNotificationsApiKey: '',
      pushNotificationsCluster: ''
    },
    classSchedule: {
      termStartDate: '',
      termEndDate: '',
      weeklyScheduleStart: '08:00',
      weeklyScheduleEnd: '17:00',
      lessonDuration: 45,
      breakDuration: 15,
      weekendClasses: false,
      holidayClasses: false
    },
    preferences: {
      language: 'en',
      timezone: 'Africa/Nairobi',
      dateFormat: 'MM/DD/YYYY',
      reportingCycle: 'monthly',
      parentAccessLevel: 'standard',
      gradingSystem: 'percentage'
    },
    integrations: {
      enableWhatsApp: false,
      enableGoogleMeet: false,
      googleMeetClientId: '',
      googleMeetApiKey: '',
      googleMeetCalendarId: ''
    }
  });
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Fetch settings data on component mount
  useEffect(() => {
    fetchSettings();
  }, []);
  
  // Fetch settings from API
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools/settings');
      
      // Set settings with response data or use defaults for missing data
      setSettings({
        notifications: {
          emailNotifications: response.data.data.notifications?.emailNotifications ?? true,
          pushNotifications: response.data.data.notifications?.pushNotifications ?? true,
          classReminders: response.data.data.notifications?.classReminders ?? true,
          newStudentAlerts: response.data.data.notifications?.newStudentAlerts ?? true,
          paymentAlerts: response.data.data.notifications?.paymentAlerts ?? true,
          marketingEmails: response.data.data.notifications?.marketingEmails ?? false,
          realTimeNotifications: response.data.data.notifications?.realTimeNotifications ?? false,
          pushNotificationsApiKey: response.data.data.notifications?.pushNotificationsApiKey ?? '',
          pushNotificationsCluster: response.data.data.notifications?.pushNotificationsCluster ?? ''
        },
        classSchedule: {
          termStartDate: response.data.data.classSchedule?.termStartDate ?? '',
          termEndDate: response.data.data.classSchedule?.termEndDate ?? '',
          weeklyScheduleStart: response.data.data.classSchedule?.weeklyScheduleStart ?? '08:00',
          weeklyScheduleEnd: response.data.data.classSchedule?.weeklyScheduleEnd ?? '17:00',
          lessonDuration: response.data.data.classSchedule?.lessonDuration ?? 45,
          breakDuration: response.data.data.classSchedule?.breakDuration ?? 15,
          weekendClasses: response.data.data.classSchedule?.weekendClasses ?? false,
          holidayClasses: response.data.data.classSchedule?.holidayClasses ?? false
        },
        preferences: {
          language: response.data.data.preferences?.language ?? 'en',
          timezone: response.data.data.preferences?.timezone ?? 'Africa/Nairobi',
          dateFormat: response.data.data.preferences?.dateFormat ?? 'MM/DD/YYYY',
          reportingCycle: response.data.data.preferences?.reportingCycle ?? 'monthly',
          parentAccessLevel: response.data.data.preferences?.parentAccessLevel ?? 'standard',
          gradingSystem: response.data.data.preferences?.gradingSystem ?? 'percentage'
        },
        integrations: {
          enableWhatsApp: response.data.data.integrations?.enableWhatsApp ?? false,
          enableGoogleMeet: response.data.data.integrations?.enableGoogleMeet ?? false,
          googleMeetClientId: response.data.data.integrations?.googleMeetClientId ?? '',
          googleMeetApiKey: response.data.data.integrations?.googleMeetApiKey ?? '',
          googleMeetCalendarId: response.data.data.integrations?.googleMeetCalendarId ?? ''
        }
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      showToast('Failed to load settings', 'error');
      setLoading(false);
    }
  };
  
  // Handle notification settings change
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [name]: checked
      }
    });
  };
  
  // Handle class schedule settings change
  const handleScheduleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSettings({
      ...settings,
      classSchedule: {
        ...settings.classSchedule,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };
  
  // Handle preference settings change
  const handlePreferenceChange = (e) => {
    const { name, value } = e.target;
    
    setSettings({
      ...settings,
      preferences: {
        ...settings.preferences,
        [name]: value
      }
    });
  };
  
  // Handle integration settings change
  const handleIntegrationChange = (e) => {
    const { name, checked } = e.target;
    
    setSettings({
      ...settings,
      integrations: {
        ...settings.integrations,
        [name]: checked
      }
    });
  };
  
  // Handle save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      await api.put('/schools/settings', settings);
      
      showToast('Settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <FaCog className="mr-2 text-primary" /> School Settings
      </h1>
      
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Notification Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaBell className="mr-2 text-primary" /> Notification Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label htmlFor="emailNotifications" className="text-gray-700">
                  Email Notifications
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="emailNotifications"
                    name="emailNotifications"
                    checked={settings.notifications.emailNotifications}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="emailNotifications"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="pushNotifications" className="text-gray-700">
                  Push Notifications
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="pushNotifications"
                    name="pushNotifications"
                    checked={settings.notifications.pushNotifications}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="pushNotifications"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="classReminders" className="text-gray-700">
                  Class Reminders
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="classReminders"
                    name="classReminders"
                    checked={settings.notifications.classReminders}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="classReminders"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="newStudentAlerts" className="text-gray-700">
                  New Student Alerts
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="newStudentAlerts"
                    name="newStudentAlerts"
                    checked={settings.notifications.newStudentAlerts}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="newStudentAlerts"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="paymentAlerts" className="text-gray-700">
                  Payment Alerts
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="paymentAlerts"
                    name="paymentAlerts"
                    checked={settings.notifications.paymentAlerts}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="paymentAlerts"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="marketingEmails" className="text-gray-700">
                  Marketing Emails
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="marketingEmails"
                    name="marketingEmails"
                    checked={settings.notifications.marketingEmails}
                    onChange={handleNotificationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="marketingEmails"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              {settings.notifications.pushNotifications && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <h3 className="text-md font-medium mb-3 flex items-center">
                    <FaComments className="mr-2 text-primary" /> Real-time Notifications Setup
                  </h3>
                  
                  <div className="flex items-center justify-between mb-3">
                    <label htmlFor="realTimeNotifications" className="text-gray-700">
                      Enable Pusher for Real-time Updates
                    </label>
                    <div className="relative inline-block w-10 mr-2 align-middle select-none">
                      <input
                        type="checkbox"
                        id="realTimeNotifications"
                        name="realTimeNotifications"
                        checked={settings.notifications.realTimeNotifications}
                        onChange={(e) => setSettings({
                          ...settings,
                          notifications: {
                            ...settings.notifications,
                            realTimeNotifications: e.target.checked
                          }
                        })}
                        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                      />
                      <label
                        htmlFor="realTimeNotifications"
                        className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                      ></label>
                    </div>
                  </div>
                  
                  {settings.notifications.realTimeNotifications && (
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="pushNotificationsApiKey" className="block text-sm font-medium text-gray-700 mb-1">
                          Pusher API Key
                        </label>
                        <input
                          type="text"
                          id="pushNotificationsApiKey"
                          name="pushNotificationsApiKey"
                          value={settings.notifications.pushNotificationsApiKey}
                          onChange={(e) => setSettings({
                            ...settings,
                            notifications: {
                              ...settings.notifications,
                              pushNotificationsApiKey: e.target.value
                            }
                          })}
                          className="form-input w-full"
                          placeholder="Enter your Pusher API Key"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="pushNotificationsCluster" className="block text-sm font-medium text-gray-700 mb-1">
                          Pusher Cluster
                        </label>
                        <input
                          type="text"
                          id="pushNotificationsCluster"
                          name="pushNotificationsCluster"
                          value={settings.notifications.pushNotificationsCluster}
                          onChange={(e) => setSettings({
                            ...settings,
                            notifications: {
                              ...settings.notifications,
                              pushNotificationsCluster: e.target.value
                            }
                          })}
                          className="form-input w-full"
                          placeholder="Enter your Pusher Cluster (e.g., eu, us, ap1)"
                        />
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-1">
                        Real-time notifications and chat updates require a Pusher account. 
                        <a href="https://pusher.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline"> 
                          Learn more
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Class Schedule Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaClock className="mr-2 text-primary" /> Class Schedule
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="termStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Term Start Date
                  </label>
                  <input
                    type="date"
                    id="termStartDate"
                    name="termStartDate"
                    value={settings.classSchedule.termStartDate}
                    onChange={handleScheduleChange}
                    className="form-input w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="termEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Term End Date
                  </label>
                  <input
                    type="date"
                    id="termEndDate"
                    name="termEndDate"
                    value={settings.classSchedule.termEndDate}
                    onChange={handleScheduleChange}
                    className="form-input w-full"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="weeklyScheduleStart" className="block text-sm font-medium text-gray-700 mb-1">
                    Daily Schedule Start
                  </label>
                  <input
                    type="time"
                    id="weeklyScheduleStart"
                    name="weeklyScheduleStart"
                    value={settings.classSchedule.weeklyScheduleStart}
                    onChange={handleScheduleChange}
                    className="form-input w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="weeklyScheduleEnd" className="block text-sm font-medium text-gray-700 mb-1">
                    Daily Schedule End
                  </label>
                  <input
                    type="time"
                    id="weeklyScheduleEnd"
                    name="weeklyScheduleEnd"
                    value={settings.classSchedule.weeklyScheduleEnd}
                    onChange={handleScheduleChange}
                    className="form-input w-full"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="lessonDuration" className="block text-sm font-medium text-gray-700 mb-1">
                    Lesson Duration (minutes)
                  </label>
                  <input
                    type="number"
                    id="lessonDuration"
                    name="lessonDuration"
                    value={settings.classSchedule.lessonDuration}
                    onChange={handleScheduleChange}
                    min="15"
                    max="180"
                    step="5"
                    className="form-input w-full"
                  />
                </div>
                
                <div>
                  <label htmlFor="breakDuration" className="block text-sm font-medium text-gray-700 mb-1">
                    Break Duration (minutes)
                  </label>
                  <input
                    type="number"
                    id="breakDuration"
                    name="breakDuration"
                    value={settings.classSchedule.breakDuration}
                    onChange={handleScheduleChange}
                    min="0"
                    max="60"
                    step="5"
                    className="form-input w-full"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <label htmlFor="weekendClasses" className="text-gray-700">
                  Allow Weekend Classes
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="weekendClasses"
                    name="weekendClasses"
                    checked={settings.classSchedule.weekendClasses}
                    onChange={handleScheduleChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="weekendClasses"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <label htmlFor="holidayClasses" className="text-gray-700">
                  Allow Holiday Classes
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="holidayClasses"
                    name="holidayClasses"
                    checked={settings.classSchedule.holidayClasses}
                    onChange={handleScheduleChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="holidayClasses"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
            </div>
          </div>
          
          {/* School Preferences */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaGlobe className="mr-2 text-primary" /> School Preferences
            </h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Language
                </label>
                <select
                  id="language"
                  name="language"
                  value={settings.preferences.language}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                  <option value="fr">French</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  value={settings.preferences.timezone}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="Africa/Nairobi">East Africa Time (EAT)</option>
                  <option value="Africa/Lagos">West Africa Time (WAT)</option>
                  <option value="Africa/Cairo">Eastern European Time (EET)</option>
                  <option value="Africa/Johannesburg">South Africa Standard Time (SAST)</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Format
                </label>
                <select
                  id="dateFormat"
                  name="dateFormat"
                  value={settings.preferences.dateFormat}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="reportingCycle" className="block text-sm font-medium text-gray-700 mb-1">
                  Reporting Cycle
                </label>
                <select
                  id="reportingCycle"
                  name="reportingCycle"
                  value={settings.preferences.reportingCycle}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="termly">Termly</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="parentAccessLevel" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Access Level
                </label>
                <select
                  id="parentAccessLevel"
                  name="parentAccessLevel"
                  value={settings.preferences.parentAccessLevel}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="limited">Limited (Grades and Attendance Only)</option>
                  <option value="standard">Standard (Grades, Attendance, Messages)</option>
                  <option value="full">Full Access (All Features)</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="gradingSystem" className="block text-sm font-medium text-gray-700 mb-1">
                  Grading System
                </label>
                <select
                  id="gradingSystem"
                  name="gradingSystem"
                  value={settings.preferences.gradingSystem}
                  onChange={handlePreferenceChange}
                  className="form-select w-full"
                >
                  <option value="percentage">Percentage (0-100%)</option>
                  <option value="letter">Letter Grades (A-F)</option>
                  <option value="gpa">GPA (0.0-4.0)</option>
                  <option value="custom">Custom Scale</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Integration Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaCog className="mr-2 text-primary" /> Integration Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label htmlFor="enableWhatsApp" className="text-gray-700">
                  WhatsApp Integration
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="enableWhatsApp"
                    name="enableWhatsApp"
                    checked={settings.integrations.enableWhatsApp}
                    onChange={handleIntegrationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="enableWhatsApp"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label htmlFor="enableGoogleMeet" className="text-gray-700">
                  Google Meet Integration
                </label>
                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    id="enableGoogleMeet"
                    name="enableGoogleMeet"
                    checked={settings.integrations.enableGoogleMeet}
                    onChange={handleIntegrationChange}
                    className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                  />
                  <label
                    htmlFor="enableGoogleMeet"
                    className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  ></label>
                </div>
              </div>
              
              {settings.integrations.enableGoogleMeet && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <h3 className="text-md font-medium mb-3">Google Meet Configuration</h3>
                  
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="googleMeetClientId" className="block text-sm font-medium text-gray-700 mb-1">
                        Google Meet Client ID
                      </label>
                      <input
                        type="text"
                        id="googleMeetClientId"
                        name="googleMeetClientId"
                        value={settings.integrations.googleMeetClientId}
                        onChange={(e) => setSettings({
                          ...settings,
                          integrations: {
                            ...settings.integrations,
                            googleMeetClientId: e.target.value
                          }
                        })}
                        className="form-input w-full"
                        placeholder="Enter your Google Meet Client ID"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="googleMeetApiKey" className="block text-sm font-medium text-gray-700 mb-1">
                        Google Meet API Key
                      </label>
                      <input
                        type="text"
                        id="googleMeetApiKey"
                        name="googleMeetApiKey"
                        value={settings.integrations.googleMeetApiKey}
                        onChange={(e) => setSettings({
                          ...settings,
                          integrations: {
                            ...settings.integrations,
                            googleMeetApiKey: e.target.value
                          }
                        })}
                        className="form-input w-full"
                        placeholder="Enter your Google Meet API Key"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="googleMeetCalendarId" className="block text-sm font-medium text-gray-700 mb-1">
                        Google Meet Calendar ID
                      </label>
                      <input
                        type="text"
                        id="googleMeetCalendarId"
                        name="googleMeetCalendarId"
                        value={settings.integrations.googleMeetCalendarId}
                        onChange={(e) => setSettings({
                          ...settings,
                          integrations: {
                            ...settings.integrations,
                            googleMeetCalendarId: e.target.value
                          }
                        })}
                        className="form-input w-full"
                        placeholder="Enter your Google Meet Calendar ID"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Save Button */}
      <div className="mt-6">
        <button
          type="button"
          onClick={handleSaveSettings}
          disabled={saving || loading}
          className="flex items-center px-6 py-3 bg-primary border border-transparent rounded-md shadow-sm text-base font-medium text-white hover:bg-primary-dark"
        >
          {saving ? <LoadingSpinner size="sm" className="mr-2" /> : <FaSave className="mr-2" />}
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default SchoolSettings; 